<template>
  <div class="rental-place-details">
    <div class="header">
      <h2>{{ isEditMode ? 'Edit Support Expenses' : rentalPlace.cost_number }}</h2>

      <div class="top-buttons">
        <button class="icon-btn" title="Edit support expense" @click="startEdit">
          <i class="bi bi-pencil"></i>
        </button>
        <button class="remove-btn" title="Delete support expense" @click="deleteRentalPlace">
          <i class="bi bi-trash"></i>
        </button>
      </div>
    </div>

    <div v-if="!isEditMode">
      <div class="detail"><strong>Cost number:</strong> {{ rentalPlace.cost_number }}</div>
      <div class="detail">
        <strong>Location:</strong>
        <router-link
          v-if="rentalPlace.location_id"
          :to="{ name: 'LocationDetailPage', params: { id: rentalPlace.location_id } }"
        >
          {{ locationName || 'Loading…' }}
        </router-link>
        <span v-else>N/A</span>
      </div>
    </div>

    <form v-else @submit.prevent="saveChanges">
      <div class="form-group">
        <label for="rental-place-cost-number">Cost number</label>
        <input id="rental-place-cost-number" v-model="editable.cost_number" type="text" required>
      </div>
      <div class="form-group">
        <label for="rental-place-location">Location</label>
        <LocationSelect
          id="rental-place-location"
          v-model="editable.location_id"
          :locations="locations"
        />
      </div>
      <div class="buttons-container">
        <button type="submit" :disabled="!editable.cost_number.trim()">Save</button>
        <button type="button" @click="isEditMode = false">Cancel</button>
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import { useToast } from 'vue-toastification';
import apiService from '@/services/apiService';
import { API_ENDPOINTS } from '@/services/config';
import { confirmDelete } from '@/utils/confirmDelete';
import LocationSelect from '@/components/LocationSelect.vue';

export default {
  name: 'RentalPlaceDetails',
  components: { LocationSelect },
  props: {
    rentalPlace: {
      type: Object,
      required: true,
    },
  },
  emits: ['update'],
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      isEditMode: false,
      editable: { cost_number: '', location_id: null },
      locations: [],
    };
  },
  computed: {
    ...mapState(['loggedInUser']),
    locationName() {
      return this.locations.find((location) => location.id === this.rentalPlace.location_id)?.name || '';
    },
  },
  created() {
    this.fetchLocations();
  },
  methods: {
    async fetchLocations() {
      try {
        const response = await axios.get(API_ENDPOINTS.locations);
        this.locations = response.data;
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      }
    },
    startEdit() {
      this.editable = {
        cost_number: this.rentalPlace.cost_number,
        location_id: this.rentalPlace.location_id,
      };
      this.isEditMode = true;
    },
    saveChanges() {
      const changes = {};
      const costNumber = this.editable.cost_number.trim();
      if (costNumber !== this.rentalPlace.cost_number) changes.cost_number = costNumber;
      if (this.editable.location_id !== this.rentalPlace.location_id) changes.location_id = this.editable.location_id;

      if (Object.keys(changes).length) {
        this.$emit('update', changes);
      }
      this.isEditMode = false;
    },
    async deleteRentalPlace() {
      const isConfirmed = await confirmDelete(
        'Delete this support expense? Its instruments are kept but will no longer belong to a support expense.'
      );
      if (!isConfirmed) return;

      try {
        await apiService.deleteResource(
          API_ENDPOINTS.rentalPlace(this.rentalPlace.id),
          this.loggedInUser?.id,
          this.loggedInUser?.entra_id
        );
        this.toast.success('Support expense deleted');
        this.$router.push({ name: 'RentalPlaceList' });
      } catch (error) {
        this.toast.error(error?.response?.data?.message || 'Failed to delete support expense');
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.rental-place-details {
  padding: 15px 20px;
  border-radius: 8px;
  background-color: $secondary-color;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    h2 {
      color: $primary-color;
      margin-bottom: 20px;
    }
  }

  .top-buttons {
    display: flex;
    gap: 10px;
  }

  .detail {
    margin-bottom: 3px;
    font-size: 16px;

    strong {
      color: black;
    }
  }

  .buttons-container {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }
}
</style>
